import { useState, useEffect, useCallback } from 'react';
import { followsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useDarkMode } from '../context/DarkModeContext';
import toast from 'react-hot-toast';
import { FaUserPlus, FaUserCheck } from 'react-icons/fa';

const FollowButton = ({ userId, username, initialFollowing = false, onFollowChange }) => {
  const { currentUser, userProfile } = useAuth();
  const { darkMode } = useDarkMode();
  const [isFollowing, setIsFollowing] = useState(initialFollowing);
  const [loading, setLoading] = useState(false);

  const fetchFollowStatus = useCallback(async () => {
    try {
      const response = await followsAPI.getStatus(userId);
      setIsFollowing(response.data.isFollowing);
    } catch (error) {
      console.error('Failed to fetch follow status:', error);
    }
  }, [userId]);

  useEffect(() => {
    // Fetch follow status if user is logged in and it's not their own profile
    if (currentUser && userProfile && userId !== userProfile.id) {
      fetchFollowStatus();
    }
  }, [currentUser, userId, userProfile, fetchFollowStatus]);

  const handleFollow = async () => {
    if (!currentUser) {
      toast.error('Please login to follow users');
      return;
    }

    setLoading(true);
    try {
      if (isFollowing) {
        await followsAPI.unfollow(userId);
        setIsFollowing(false);
        toast.success(`Unfollowed ${username}`);
      } else {
        await followsAPI.follow(userId);
        setIsFollowing(true);
        toast.success(`Following ${username}`);
      }

      // Notify parent component of follow change
      if (onFollowChange) {
        onFollowChange(!isFollowing);
      }
    } catch (error) {
      console.error('Follow action error:', error);
      toast.error(error.response?.data?.message || 'Failed to update follow status');
    } finally {
      setLoading(false);
    }
  };

  // Don't show button if viewing own profile
  if (!currentUser || !userProfile || userId === userProfile.id) {
    return null;
  }

  return (
    <button
      onClick={handleFollow}
      disabled={loading}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition disabled:opacity-50 ${
        isFollowing
          ? darkMode
            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300 border border-gray-300'
          : darkMode
          ? 'bg-purple-600 text-white hover:bg-purple-700'
          : 'bg-boop-primary text-white hover:bg-boop-secondary'
      }`}
    >
      {isFollowing ? (
        <>
          <FaUserCheck />
          <span>Following</span>
        </>
      ) : (
        <>
          <FaUserPlus />
          <span>Follow</span>
        </>
      )}
    </button>
  );
};

export default FollowButton;
